import { Controller, useFormContext, useFormState } from "react-hook-form";
import type { ICheckboxProps, IFormFieldProps } from "@/ui-kit";
import { Checkbox, CheckboxCard, FormField } from "@/ui-kit";
import { get } from "../utils";

type Props = Omit<ICheckboxProps, "checked" | "onChange"> &
  Omit<
    IFormFieldProps,
    "label" | "description" | "htmlFor" | "tooltipContent" | "children"
  > & {
    name: string;
    value?: string;
    formElementClass?: string;
    onChange?: (checked: boolean) => void;
    variant?: "default" | "card";
  };

export const CheckboxControl = (props: Props) => {
  const {
    message,
    required,
    accessory,
    formElementClass,
    name,
    value,
    onChange,
    variant = "default",
    ...restCheckboxProps
  } = props;
  const { control } = useFormContext();
  const { errors } = useFormState();
  const errorMessage = get(errors, name)?.message as string | undefined;

  const checkboxElement = (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange: onFieldChange, value: fieldValue } }) => {
        const handleChange = (checked: boolean) => {
          if (value) {
            const currentArray = Array.isArray(fieldValue) ? fieldValue : [];
            const newArray = checked
              ? [...currentArray, value]
              : currentArray.filter((item) => item !== value);
            onFieldChange(newArray);
          } else {
            onFieldChange(checked);
          }

          if (onChange) {
            onChange(checked);
          }
        };

        const isChecked = value
          ? Array.isArray(fieldValue) && fieldValue.includes(value)
          : !!fieldValue;

        const Component = variant === "card" ? CheckboxCard : Checkbox;

        return (
          <Component
            {...restCheckboxProps}
            checked={isChecked}
            onChange={handleChange}
            isCard={variant === "card"}
          />
        );
      }}
    />
  );

  return (
    <FormField
      message={message ?? errorMessage}
      required={required}
      className={formElementClass}
      accessory={accessory}
    >
      {checkboxElement}
    </FormField>
  );
};
